/**
 * Root route — DESIGN.md §13.1.
 *
 * Everything below this route is authenticated. The guard lives here rather
 * than in each route so a new screen is protected by existing, not by
 * remembering to add a check.
 */
import {
  Outlet,
  createRootRouteWithContext,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import { RealtimeProvider } from '@/lib/realtime';
import { AppShell } from '@/components/app-shell';
import { CommandPalette } from '@/components/command-palette';
import { GlobalHotkeys } from '@/components/global-hotkeys';
import { NewCardHost } from '@/components/cards/new-card-host';
import { PushBridge } from '@/components/push-bridge';
import { useTextSize } from '@/lib/prefs';
import { useTheme } from '@/lib/theme';
import { LoadingRegion } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { reportError } from '@/lib/sentry';

export interface RouterContext {
  queryClient: QueryClient;
}

/** Routes reachable without a session. */
const PUBLIC_ROUTES = ['/login', '/invite'];

/**
 * Only ever redirect to a path on this origin. A `returnTo` arrives from the
 * query string, so an absolute URL there would be an open redirect.
 */
function internalPath(value: unknown): string {
  if (typeof value !== 'string') return '/';
  if (!value.startsWith('/') || value.startsWith('//')) return '/';
  return value;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RouteError,
});

/**
 * The last resort — §13.5 applies to failure too: say what happened, and offer
 * the way out. The error is reported first, so a screen someone can recover
 * from is still a screen we hear about.
 */
function RouteError({ error }: { error: Error }): JSX.Element {
  useEffect(() => {
    reportError(error, { boundary: 'route' });
  }, [error]);

  return (
    <div className="mx-auto w-full max-w-2xl p-6">
      <EmptyState
        title="That screen did not load"
        description="Something went wrong rendering this page. It has been reported. Reloading usually clears it — if it does not, tell Brad what you were doing."
        action={
          <Button onClick={() => window.location.reload()} variant="outline">
            Reload
          </Button>
        }
      />
    </div>
  );
}

function RootLayout(): JSX.Element {
  // Per-device text size (Settings › Appearance) — applied to <html data-text>.
  useTextSize();
  return (
    <AuthProvider>
      {/* §15 — one SSE connection per tab, opened once a session exists. */}
      <RealtimeProvider>
        <AuthGate />
      </RealtimeProvider>
      {/* Bottom-left: bottom-right sat on top of the card panel's Comment button (CARD-24). */}
      <AppToaster />
    </AuthProvider>
  );
}

/**
 * Toasts — #24.
 *
 * Sonner defaults to `theme="light"` and has no idea what the app is set to, so
 * every toast rendered a light card against a dark UI; with `richColors` on, a
 * success came out pale green with a dark action button in it. It needs telling
 * which theme it is inside, and it is the only component here that does — hence
 * a wrapper rather than a prop at the call site.
 */
function AppToaster(): JSX.Element {
  const { theme } = useTheme();
  return <Toaster position="bottom-left" theme={theme} richColors closeButton />;
}

function AuthGate(): JSX.Element {
  const { me, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useRouterState({ select: (state) => state.location });
  const pathname = location.pathname;
  const isPublic = PUBLIC_ROUTES.some((route) => pathname.startsWith(route));

  /**
   * All redirection lives here, and nowhere else. The login and invite screens
   * deliberately do *not* navigate on success: two components racing to
   * redirect on the same state change produced a visible bounce through
   * `/login` on every sign-in.
   */
  useEffect(() => {
    if (isLoading) return;

    if (!me) {
      if (isPublic) return;
      // Remember where they were headed so the round trip lands them back.
      void navigate({
        to: '/login',
        search: pathname === '/' ? {} : { returnTo: pathname + location.searchStr },
        replace: true,
      });
      return;
    }

    if (isPublic) {
      const returnTo = (location.search as { returnTo?: unknown }).returnTo;
      void navigate({ to: internalPath(returnTo), replace: true });
    }
  }, [me, isLoading, isPublic, pathname, location.search, location.searchStr, navigate]);

  if (isLoading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <LoadingRegion label="Loading Slate" />
      </div>
    );
  }

  if (isPublic) return <Outlet />;
  if (!me) {
    // The redirect above is in flight; rendering the shell would flash an
    // empty signed-in layout first.
    return (
      <div className="grid min-h-dvh place-items-center">
        <LoadingRegion label="Redirecting" />
      </div>
    );
  }

  return (
    <AppShell>
      {/* §13.5 — ⌘K anywhere, behind the auth guard like every other surface. */}
      <CommandPalette />
      <GlobalHotkeys />
      <NewCardHost />
      <PushBridge userId={me.id} />
      <Outlet />
    </AppShell>
  );
}
